import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Shield, Clock, Leaf, ChevronRight, Phone } from 'lucide-react'
import { services, whyUs, companyInfo, seoMeta } from '../data/pages'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import { getIcon } from '../components/IconMap'
import SEO from '../components/SEO'

export default function HomePage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation()
  const { ref: servicesRef, isVisible: servicesVisible } = useScrollAnimation()
  const { ref: trustRef, isVisible: trustVisible } = useScrollAnimation()

  return (
    <>
      <SEO title={seoMeta.home.title} description={seoMeta.home.description} />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-earth-900">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blossom-950 via-earth-900 to-prime-950" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-blossom-500/20 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-prime-500/15 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '4s' }} />
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'linear-gradient(rgba(34,197,94,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
        </div>

        <div className="container-wide section-padding relative z-10 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div ref={heroRef} initial={{ opacity: 0, x: -40 }} animate={heroVisible ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blossom-500/10 border border-blossom-500/20 text-blossom-300 text-sm font-medium mb-6">
                <Leaf className="w-4 h-4" />Professional Facility Cleaning — UK Nationwide
              </div>
              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight">
                Clean Spaces.<br /><span className="gradient-text-animated">Safe Environments.</span><br />Better Living.
              </h1>
              <p className="text-lg text-earth-300 leading-relaxed mb-8 max-w-xl">
                Dependable, professional cleaning services for construction, healthcare, property and student accommodation — delivered to a consistent standard, on time and with minimal disruption.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-base">Get a Free Quote<ArrowRight className="w-5 h-5" /></Link>
                <Link to="/services" className="btn-ghost text-base text-white hover:text-blossom-300 hover:bg-white/5">Explore Our Services<ChevronRight className="w-5 h-5" /></Link>
              </div>
              <div className="flex items-center gap-6 mt-10 text-sm text-earth-400">
                <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-blossom-400" />Fully Insured</span>
                <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-blossom-400" />Flexible Scheduling</span>
                <span className="flex items-center gap-2"><Leaf className="w-4 h-4 text-blossom-400" />Eco-Responsible</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 40 }} animate={heroVisible ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.2, 1] }} className="relative hidden lg:block">
              <div className="relative pb-10">
                <div className="absolute -inset-4 bg-gradient-to-r from-blossom-500/20 to-prime-500/20 rounded-3xl blur-2xl" />
                <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]">
                  <img
                    src="/images/1_Robot_Hand_Saving_Earth_with_Crystal.png"
                    alt="Eco-friendly technology protecting the planet"
                    className="w-full h-full object-cover object-center"
                    loading="eager"
                  />
                </div>
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute bottom-0 -left-6 glass-card px-6 py-4 rounded-2xl shadow-elevated"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blossom-100 rounded-xl flex items-center justify-center"><Leaf className="w-6 h-6 text-blossom-600" /></div>
                    <div><div className="text-2xl font-bold text-earth-900">100%</div><div className="text-xs text-earth-500">Biodegradable Products</div></div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding-y section-padding bg-earth-50">
        <div className="container-wide">
          <motion.div ref={servicesRef} initial={{ opacity: 0, y: 30 }} animate={servicesVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-16">
            <span className="badge-green mb-4">Our Services</span>
            <h2 className="text-display-2 font-bold text-earth-900 mb-4">Specialist Cleaning for Every Environment</h2>
            <p className="text-earth-500 text-lg max-w-2xl mx-auto">We focus on sectors where cleaning standards matter most — where presentation, hygiene and compliance directly affect the people who use the space.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = getIcon(service.icon)
              const iconBg = service.color === 'blossom' ? 'bg-blossom-100 text-blossom-600' : service.color === 'prime' ? 'bg-prime-100 text-prime-600' : 'bg-accent-100 text-accent-600'
              return (
                <motion.div key={service.id} initial={{ opacity: 0, y: 30 }} animate={servicesVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: index * 0.1 }}>
                  <Link to="/services" className="group block h-full bg-white rounded-2xl p-6 shadow-sm hover:shadow-elevated transition-all duration-500 hover:-translate-y-2 border border-earth-100">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${iconBg}`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-bold text-earth-900 mb-2 group-hover:text-blossom-700 transition-colors">{service.title}</h3>
                    <p className="text-earth-500 text-sm leading-relaxed mb-4">{service.description.slice(0, 100)}...</p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-blossom-600 group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-4 h-4" /></span>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Trust / Why Us Preview */}
      <section className="section-padding-y section-padding bg-earth-50">
        <div className="container-wide">
          <motion.div ref={trustRef} initial={{ opacity: 0, y: 30 }} animate={trustVisible ? { opacity: 1, y: 0 } : {}} className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="badge-blue mb-4">Why Blossom Prime</span>
              <h2 className="text-display-2 font-bold text-earth-900 mb-6">Professional, Reliable and <span className="gradient-text">Responsible</span></h2>
              <p className="text-earth-500 text-lg leading-relaxed mb-8">We combine trained operatives, structured quality control and environmentally responsible products to deliver results our clients can count on — consistently, across every site.</p>
              <div className="space-y-4 mb-8">
                {whyUs.differentiators.slice(0, 4).map((item) => {
                  const Icon = getIcon(item.icon)
                  return (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-blossom-100 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-blossom-600" />
                      </div>
                      <div><h4 className="font-semibold text-earth-900">{item.title}</h4><p className="text-earth-500 text-sm">{item.description}</p></div>
                    </div>
                  )
                })}
              </div>
              <Link to="/why-us" className="btn-primary">Discover Our Difference<ArrowRight className="w-5 h-5" /></Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blossom-500/10 to-prime-500/10 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]">
                <img src="/images/7_Future_eco_friendly_city_illustration.png" alt="Future eco-friendly city with green buildings" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding-y section-padding bg-earth-900">
        <div className="container-wide text-center">
          <h2 className="text-display-2 font-bold text-white mb-4">Ready for a Cleaner Space?</h2>
          <p className="text-earth-300 text-lg max-w-2xl mx-auto mb-8">Get a clear, no-obligation quotation within 24 hours. Our team is ready to discuss your cleaning requirements and put together the right solution for your site.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary text-lg px-8 py-4">Get Your Free Quote<ArrowRight className="w-5 h-5" /></Link>
            <a href={`tel:${companyInfo.contact.phone.replace(/\s/g, '')}`} className="btn-secondary text-lg px-8 py-4"><Phone className="w-5 h-5" />{companyInfo.contact.phone}</a>
          </div>
        </div>
      </section>
    </>
  )
}
